import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, FileText, Check, Download, Eye, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { DocumentUploadButton } from '@/components/documents/DocumentUploadButton';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { FinalizeWithSignatures } from '@/components/documents/FinalizeWithSignatures';
import DocumentEmailWizard from '@/components/documents/DocumentEmailWizard';
import { embedSignaturesIntoHtml } from '@/lib/signatureEmbedding';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  renderCanonResolutionHtml,
  renderStandardFormHtml,
  type CanonRenderInputs,
} from './canonResolutionHtml';
import {
  StandardResolutionPicker,
  type StandardResolutionRow,
} from './StandardResolutionPicker';
import { DocumentSigningControl } from '../DocumentSigningControl';
import { SealApplyCheckbox } from '../SealApplyCheckbox';
import { useAssociationSeal } from '../useAssociationSeal';
import { generatedFilePath } from '../docProvenance';
import { finishLegacyHtml } from '../legacyDocumentFinish';
import { legacySigningMeta } from '../legacySigningSpecs';
import { downloadDocumentById } from '@/lib/downloadDocumentById';


interface ResolutionPhase1Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

export function ResolutionPhase1({ task, phaseData, onPhaseComplete }: ResolutionPhase1Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const seal = useAssociationSeal(task.una_id);
  const [generating, setGenerating] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<any | null>(null);
  const [unaData, setUnaData] = useState<any | null>(null);
  const [treasurySecretaryProfile, setTreasurySecretaryProfile] = useState<any | null>(null);
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [selectedDocForSignature, setSelectedDocForSignature] = useState<any | null>(null);
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<string | null>(null);

  // Adoption + signing officer configuration (canon structure inputs).
  const [adoptionMode, setAdoptionMode] = useState<'meeting' | 'written'>(
    (task.workflow_data?.adoption_mode as 'meeting' | 'written') || 'written',
  );
  const [votesFor, setVotesFor] = useState<string>(String(task.workflow_data?.votes_for ?? ''));
  const [votesAgainst, setVotesAgainst] = useState<string>(String(task.workflow_data?.votes_against ?? ''));
  const [meetingDate, setMeetingDate] = useState<string>(task.workflow_data?.adoption_meeting_date || '');
  const [signingOfficerName, setSigningOfficerName] = useState<string>(task.workflow_data?.signing_officer_name || '');
  const [signingOfficerOffice, setSigningOfficerOffice] = useState<string>(task.workflow_data?.signing_officer_office || '');

  // Standard form picker state.
  const [pendingStandard, setPendingStandard] = useState<{
    row: StandardResolutionRow;
    values: Record<string, string>;
  } | null>(null);

  const workflowData = task.workflow_data || {};

  // Get the resolution title from workflow data
  const resolutionTitle = workflowData.resolution_title || 'Custom Resolution';

  // Required documents for Phase 1
  const requiredDocuments = [
    { 
      key: 'custom_resolution', 
      title: resolutionTitle,
      requiresSignature: true
    },
  ];

  useEffect(() => {
    loadDocuments();
    loadUnaData();
  }, [task.id]);

  async function loadUnaData() {
    if (!task.una_id) return;
    
    const { data, error } = await supabase
      .from('unas')
      .select(`
        *,
        una_members(
          id,
          user_id,
          full_name,
          email,
          role,
          status
        )
      `)
      .eq('id', task.una_id)
      .maybeSingle();

    if (!error && data) {
      setUnaData(data);
      
      // Fetch Secretary Treasurer profile using treasury_secretary_id
      if (data.treasury_secretary_id) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .eq('id', data.treasury_secretary_id)
          .single();
        
        if (profileData) {
          setTreasurySecretaryProfile(profileData);
          if (!signingOfficerName) setSigningOfficerName(profileData.full_name || '');
        }
      }
      if (!signingOfficerOffice) {
        setSigningOfficerOffice(data.primary_officer_role || 'Secretary Treasurer');
      }
    }
  }

  async function loadDocuments() {
    const { data, error } = await supabase
      .from('generated_documents')
      .select('*')
      .eq('task_id', task.id)
      .order('generated_at', { ascending: true }) as { data: any[] | null; error: any };

    if (error) {
      console.error('Error loading documents:', error);
      return;
    }

    // Filter to phase 1 documents
    const phase1Docs = (data || []).filter(d => {
      const templateKey = d.merge_field_data?.template_key || '';
      return templateKey && templateKey.includes('custom_resolution');
    });

    setDocuments(phase1Docs);
  }

  function generateResolutionDocumentHtml(docKey: string, workflowData: any, unaData: any, tsProfile: any): string {
    const formatDate = (dateStr: string) => {
      if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const unaName = unaData?.name || '[UNA Name]';
    
    // Get Secretary Treasurer info from profile (fetched via treasury_secretary_id)
    const treasurySecretaryName = tsProfile?.full_name || '[Secretary Treasurer Name]';
    const treasurySecretaryUserId = unaData?.treasury_secretary_id || 'treasury_secretary';
    
    const resolutionTitle = workflowData.resolution_title || 'Untitled Resolution';
    const effectiveDate = formatDate(workflowData.resolution_date);
    const whereasClausesArray = workflowData.whereas_clauses || [];
    const resolvedClausesArray = workflowData.resolved_clauses || [];
    const resolutionNumber = workflowData.resolution_number || '';
    const resolutionCategory = workflowData.resolution_category || '';
    const requiresBoardApproval = workflowData.requires_board_approval || false;
    const implementationNotes = workflowData.implementation_notes || '';

    if (docKey === 'resolution_format_guide') {
      return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Resolution Format Guide</title>
  <style>
    @page { 
      size: letter; 
      margin: 1in; 
      @bottom-center { 
        content: "Page " counter(page) " of " counter(pages); 
        font-family: Cambria, Georgia, serif; 
        font-size: 10pt; 
        padding-bottom: 0.25in; 
      } 
    }
    body { 
      font-family: Cambria, Georgia, serif; 
      line-height: 1.5; 
      color: #333; 
      margin: 0; 
      padding: 0; 
    }
    .header { 
      text-align: center; 
      margin-bottom: 30px; 
    }
    .header h1 { 
      font-size: 18pt; 
      font-weight: bold; 
      margin-bottom: 10px; 
    }
    .header h2 { 
      font-size: 14pt; 
      font-weight: normal; 
      margin-bottom: 5px; 
    }
    .content { 
      margin-bottom: 20px; 
    }
    .section-title { 
      font-size: 12pt; 
      font-weight: bold; 
      margin-top: 20px; 
      margin-bottom: 10px; 
    }
    p { 
      margin-bottom: 10px; 
      text-align: justify; 
    }
    ul, ol { 
      margin-left: 30px; 
      margin-bottom: 15px; 
    }
    li { 
      margin-bottom: 8px; 
    }
    .example { 
      background-color: #f5f5f5; 
      padding: 15px; 
      margin: 15px 0; 
      border-left: 3px solid #666; 
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Resolution Format Guide</h1>
    <h2>${unaName}</h2>
  </div>
  
  <div class="content">
    <div class="section-title">Standard Resolution Format</div>
    <p>This guide provides the standard format for UNA resolutions. All resolutions should follow this structure:</p>
    
    <ol>
      <li><strong>Title</strong>: A clear, descriptive title beginning with "Resolution Concerning..."</li>
      <li><strong>WHEREAS Clauses</strong>: Background statements explaining the context and reasons</li>
      <li><strong>RESOLVED Clauses</strong>: The actual decisions and actions being taken</li>
      <li><strong>Effective Date</strong>: When the resolution takes effect</li>
      <li><strong>Signatures</strong>: Required approval signatures</li>
    </ol>

    <div class="section-title">WHEREAS Clauses</div>
    <p>WHEREAS clauses provide the factual background and justification for the resolution. Each clause should:</p>
    <ul>
      <li>Begin with "WHEREAS" in all capital letters</li>
      <li>State a single fact or reason</li>
      <li>End with a semicolon (;) except the last clause which ends with "and"</li>
      <li>Be written in formal language</li>
    </ul>

    <div class="example">
      <strong>Example WHEREAS Clauses:</strong><br/><br/>
      WHEREAS, the Board has determined that additional financial controls are necessary;<br/><br/>
      WHEREAS, the Association requires enhanced oversight of treasury operations; and<br/><br/>
      WHEREAS, these measures will protect the Association's assets;
    </div>

    <div class="section-title">RESOLVED Clauses</div>
    <p>RESOLVED clauses state the actual decisions, actions, or policies being adopted. Each clause should:</p>
    <ul>
      <li>Begin with "RESOLVED" in all capital letters</li>
      <li>State a clear action or decision</li>
      <li>Be specific and actionable</li>
      <li>End with a period (.) for the final clause</li>
    </ul>

    <div class="example">
      <strong>Example RESOLVED Clauses:</strong><br/><br/>
      RESOLVED, that the Board hereby establishes a Finance Committee;<br/><br/>
      RESOLVED, that said Committee shall meet quarterly; and be it further<br/><br/>
      RESOLVED, that this resolution shall take effect immediately upon adoption.
    </div>

    <div class="section-title">Best Practices</div>
    <ul>
      <li>Keep language clear, formal, and professional</li>
      <li>Number resolutions chronologically (e.g., Resolution 2025-001)</li>
      <li>Include the date of adoption</li>
      <li>Obtain required signatures before finalizing</li>
      <li>File in governance records after approval</li>
    </ul>
  </div>
</body>
</html>`;
    }

    if (docKey === 'custom_resolution') {
      // Build board signature blocks if the resolution needs board attestation.
      let boardSignatureBlocks = '';
      if (requiresBoardApproval && unaData?.una_members) {
        const boardMembers = unaData.una_members.filter((m: any) =>
          m.status === 'active' && m.role &&
          (m.role.toLowerCase().includes('board') || m.role.toLowerCase().includes('member'))
        );
        boardSignatureBlocks = boardMembers.map((member: any) => `
    <div class="signature-block">
      <p><strong>Board Member Approval:</strong></p>
      <div class="signature-line" data-signer-id="${member.id}" data-signature-role="board_member"></div>
      <div class="signature-label">${member.full_name}, Board Member</div>
      <div style="margin-top: 0.15in;">Date: ${effectiveDate}</div>
    </div>`).join('\n');
      }

      const canonInputs: CanonRenderInputs = {
        resolution_number: resolutionNumber || '',
        resolution_title: resolutionTitle,
        whereas_clauses: whereasClausesArray,
        resolved_clauses: resolvedClausesArray,
        adoption: {
          mode: adoptionMode,
          votes_for: votesFor,
          votes_against: votesAgainst,
          meeting_date: meetingDate || workflowData.resolution_date,
        },
        adoption_date: workflowData.resolution_date,
        signing_officer: {
          name: signingOfficerName || treasurySecretaryName,
          office: signingOfficerOffice || unaData?.primary_officer_role || 'Secretary Treasurer',
        },
        signer_id: treasurySecretaryUserId,
        una: {
          name: unaData?.name,
          legal_name: unaData?.legal_name,
          ca_filing_number: unaData?.ca_filing_number,
          ein: unaData?.ein,
          address: unaData?.address,
        },
        extra_signature_blocks_html: boardSignatureBlocks,
      };
      return renderCanonResolutionHtml(canonInputs);
    }

    return '';
  }

  // Best-effort persistence of adoption & signing officer inputs so they survive
  // a browser refresh. Non-fatal if it fails.
  async function persistWorkflowConfig() {
    try {
      const nextData = {
        ...(task.workflow_data || {}),
        adoption_mode: adoptionMode,
        votes_for: votesFor,
        votes_against: votesAgainst,
        adoption_meeting_date: meetingDate,
        signing_officer_name: signingOfficerName,
        signing_officer_office: signingOfficerOffice,
      };
      await supabase
        .from('task_workflows')
        .update({ workflow_data: nextData })
        .eq('task_id', task.id);
    } catch (e) {
      console.warn('persistWorkflowConfig non-fatal error', e);
    }
  }

  function buildCanonInputsForFinalize(withNumber?: string | number): CanonRenderInputs {
    const treasurySecretaryUserId = unaData?.treasury_secretary_id || 'treasury_secretary';
    return {
      resolution_number: withNumber ?? (workflowData.resolution_number || ''),
      resolution_title: workflowData.resolution_title || 'Untitled Resolution',
      whereas_clauses: workflowData.whereas_clauses || [],
      resolved_clauses: workflowData.resolved_clauses || [],
      adoption: {
        mode: adoptionMode,
        votes_for: votesFor,
        votes_against: votesAgainst,
        meeting_date: meetingDate || workflowData.resolution_date,
      },
      adoption_date: workflowData.resolution_date,
      signing_officer: {
        name: signingOfficerName || treasurySecretaryProfile?.full_name || 'Secretary Treasurer',
        office: signingOfficerOffice || unaData?.primary_officer_role || 'Secretary Treasurer',
      },
      signer_id: treasurySecretaryUserId,
      una: {
        name: unaData?.name,
        legal_name: unaData?.legal_name,
        ca_filing_number: unaData?.ca_filing_number,
        ein: unaData?.ein,
        address: unaData?.address,
      },
    };
  }

  async function uploadPdfFromHtml(html: string): Promise<{ blob: Blob; size: number }> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ html, author: unaData?.name || undefined }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'PDF generation failed');
    }
    const { pdf: pdfBase64, size } = await response.json();
    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
    return { blob: new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' }), size };
  }

  async function handleGenerateDocument(docReq: any) {
    setGenerating(docReq.key);

    try {
      await persistWorkflowConfig();

      // Determine content source: standard form (from picker) or freeform builder.
      let htmlContent = '';
      let templateSource: 'freeform' | 'standard' = 'freeform';
      let standardTemplateId: string | null = null;
      let standardValues: Record<string, string> | undefined;

      if (pendingStandard) {
        templateSource = 'standard';
        standardTemplateId = pendingStandard.row.id;
        standardValues = pendingStandard.values;
        const canonInputs = buildCanonInputsForFinalize(workflowData.resolution_number || '');
        htmlContent = renderStandardFormHtml(
          pendingStandard.row.template_content,
          pendingStandard.values,
          canonInputs,
        );
      } else {
        htmlContent = generateResolutionDocumentHtml(docReq.key, workflowData, unaData, treasurySecretaryProfile);
      }

      if (!htmlContent) throw new Error('Failed to generate document content');

      htmlContent = finishLegacyHtml(htmlContent, {
        entityName: unaData?.name,
        sealEnabled: seal.sealEnabled,
        sealSvg: seal.sealSvg,
      });

      const { blob: pdfBlob, size } = await uploadPdfFromHtml(htmlContent);

      const filePath = generatedFilePath(unaData?.name, docReq.key, task.id);
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, { contentType: 'application/pdf', upsert: true });
      if (uploadError) throw uploadError;

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const createdBy = task.user_id || authSession?.user?.id || null;

      const documentName = docReq.key === 'custom_resolution'
        ? (workflowData.resolution_title || docReq.title)
        : docReq.title;

      const documentRecord = {
        task_id: task.id,
        una_id: task.una_id,
        document_name: documentName,
        document_type: 'workflow',
        file_url: filePath,
        file_path: filePath,
        file_size: size,
        status: docReq.requiresSignature ? 'draft' : 'final',
        custom_content: htmlContent,
        created_by: createdBy,
        merge_field_data: {
          phase: 'phase_1',
          template_key: docReq.key,
          ...legacySigningMeta(docReq.key, seal.sealEnabled),
          template_source: templateSource,
          template_id: standardTemplateId,
          standard_values: standardValues,
          adoption_mode: adoptionMode,
          votes_for: votesFor,
          votes_against: votesAgainst,
          adoption_meeting_date: meetingDate,
          signing_officer_name: signingOfficerName,
          signing_officer_office: signingOfficerOffice,
          workflow_data: workflowData,
        },
      };

      const { error: insertError } = await supabase
        .from('generated_documents')
        .insert(documentRecord)
        .select()
        .single();
      if (insertError) throw insertError;

      toast({
        title: 'Document Generated',
        description: `${documentName} has been created successfully.`,
      });

      setPendingStandard(null);
      await loadDocuments();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate document',
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  }

  async function handleCollectSignature(doc: any) {
    setSelectedDocForSignature(doc);
    setSignatureWizardOpen(true);
  }

  async function handleSignaturesComplete(signatureResult: any) {
    if (!selectedDocForSignature) return;

    try {
      // Use treasury_secretary_id from unas table directly - matches HTML generation!
      const treasurySecretaryUserId = unaData?.treasury_secretary_id || 'treasury_secretary';

      // Extract Secretary Treasurer signature
      const tsSignature = signatureResult.treasury_secretary_signature;
      const tsDate = signatureResult.treasury_secretary_signature_date;

      if (!tsSignature) {
        throw new Error('Secretary Treasurer signature is required');
      }

      // Fetch FRESH document row so we can also see any prior allocation.
      const { data: freshDocData, error: fetchError } = await supabase
        .from('generated_documents')
        .select('custom_content, merge_field_data')
        .eq('id', selectedDocForSignature.id)
        .single();

      if (fetchError) throw fetchError;
      if (!freshDocData?.custom_content) {
        throw new Error('Document HTML not found');
      }

      // Allocate a lifetime resolution number BEFORE the final render, so the
      // number prints in the title. Idempotent: reuse any prior allocation
      // stamped on merge_field_data.
      const mfd: any = (freshDocData as any).merge_field_data || {};
      let assignedNumber: number | null = null;
      if (mfd.resolution_number && Number(mfd.resolution_number) > 0) {
        assignedNumber = Number(mfd.resolution_number);
      } else if (task.una_id) {
        try {
          const { data: numData, error: numErr } = await supabase.rpc(
            'allocate_resolution_number',
            { _una_id: task.una_id, _requested: null } as any,
          );
          if (numErr) throw numErr;
          assignedNumber = numData as unknown as number;
        } catch (allocErr) {
          console.error('Resolution number allocation failed (non-fatal):', allocErr);
        }
      }

      // Re-render HTML with allocated number so the printed title includes it.
      let baseHtml = freshDocData.custom_content;
      if (assignedNumber) {
        const templateSource = mfd.template_source;
        if (templateSource === 'standard' && mfd.template_id) {
          // Fetch template content again to re-merge with the number filled in.
          const { data: tpl } = await supabase
            .from('document_templates')
            .select('template_content')
            .eq('id', mfd.template_id)
            .maybeSingle();
          if (tpl?.template_content) {
            const canonInputs = buildCanonInputsForFinalize(assignedNumber);
            baseHtml = renderStandardFormHtml(
              tpl.template_content,
              mfd.standard_values || {},
              canonInputs,
            );
          }
        } else {
          const canonInputs = buildCanonInputsForFinalize(assignedNumber);
          // Preserve extra board sig blocks by re-running the freeform generator
          // with an updated workflow_data that includes the resolution_number.
          const augmentedWfd = { ...(workflowData || {}), resolution_number: String(assignedNumber) };
          baseHtml = generateResolutionDocumentHtml(
            'custom_resolution',
            augmentedWfd,
            unaData,
            treasurySecretaryProfile,
          );
          // If for some reason renderer returned empty, fall back to direct canon call.
          if (!baseHtml) baseHtml = renderCanonResolutionHtml(canonInputs);
        }
      }

      const signatureData = [{
        role: 'treasury_secretary',
        signer_id: treasurySecretaryUserId,
        signature_data: tsSignature,
        signed_at: tsDate || new Date().toISOString()
      }];

      const updatedHtml = await embedSignaturesIntoHtml(
        baseHtml,
        signatureData,
        new Date().toISOString()
      );

      // Generate new PDF with signatures
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ html: updatedHtml, author: unaData?.name || undefined }),
        }
      );

      if (!response.ok) throw new Error('PDF generation failed');

      const { pdf: pdfBase64 } = await response.json();

      // Convert and upload
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

      const signedFileName = generatedFilePath(unaData?.name, `${selectedDocForSignature.document_name}_signed`, selectedDocForSignature.id);
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(signedFileName, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Buckets are private — store path as reference

      // Update document record
      const { error: updateError } = await supabase
        .from('generated_documents')
        .update({
          status: 'finalized',
          finalized_at: new Date().toISOString(),
          finalized_by: task.user_id,
          file_url: signedFileName,
          file_path: signedFileName,
          file_size: pdfBlob.size,
          signed_file_url: signedFileName,
          custom_content: updatedHtml,
          merge_field_data: {
            ...(mfd || {}),
            resolution_number: assignedNumber ? String(assignedNumber) : (mfd?.resolution_number || null),
          },
          signature_history: {
            finalized_signatures: {
              treasury_secretary_signature: signatureResult.treasury_secretary_signature,
              treasury_secretary_date: signatureResult.treasury_secretary_signature_date,
              finalized_at: new Date().toISOString(),
            }
          }
        })
        .eq('id', selectedDocForSignature.id);

      if (updateError) throw updateError;

      toast({
        title: 'Document Finalized',
        description: 'Signatures have been applied successfully.',
      });

      setSignatureWizardOpen(false);
      setSelectedDocForSignature(null);
      await loadDocuments();
    } catch (error: any) {
      console.error('Error finalizing document:', error);
      toast({
        title: 'Finalization Failed',
        description: error.message || 'Failed to finalize document',
        variant: 'destructive',
      });
    }
  }

  async function handleMarkPhaseComplete() {
    // Check all required documents generated
    const missingDocs = requiredDocuments.filter(
      req => !documents.find(d => d.merge_field_data?.template_key === req.key)
    );

    if (missingDocs.length > 0) {
      toast({
        title: 'Documents Required',
        description: 'Please generate all required documents before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    // Check all signatures collected
    const unsignedDocs = documents.filter(
      d => {
        const req = requiredDocuments.find(r => r.key === d.merge_field_data?.template_key);
        return req?.requiresSignature && d.status === 'draft';
      }
    );

    if (unsignedDocs.length > 0) {
      toast({
        title: 'Signatures Required',
        description: 'Please collect all required signatures before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Phase 1 Complete',
      description: 'Resolution documents prepared. Proceeding to Phase 2.',
    });

    onPhaseComplete();
  }

  function getDocumentStatus(docKey: string) {
    const doc = documents.find(d => d.merge_field_data?.template_key === docKey);
    if (!doc) return { status: 'not_generated', doc: null };
    
    if (doc.status === 'finalized') {
      return { status: 'complete', doc };
    }
    
    const req = requiredDocuments.find(r => r.key === docKey);
    if (req?.requiresSignature && doc.status === 'draft') {
      return { status: 'needs_signature', doc };
    }
    
    return { status: 'complete', doc };
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CardTitle>Phase 1: Draft Resolution</CardTitle>
                <Badge>Current Phase</Badge>
              </div>
              <p className="text-muted-foreground">
                Create resolution documents and collect Secretary Treasurer approval
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Steps to Complete */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Steps to Complete:</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Review resolution format guide</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Draft custom resolution with WHEREAS and RESOLVED clauses</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Obtain Secretary Treasurer signature</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span>Request board member signatures</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Standard resolutions picker (renders only when published forms exist) */}
      <StandardResolutionPicker
        onUseStandard={(row, values) => setPendingStandard({ row, values })}
      />
      {pendingStandard && (
        <Alert className="border-primary/40">
          <AlertDescription className="flex items-center justify-between gap-3">
            <span>
              Using standard resolution: <strong>{pendingStandard.row.template_name}</strong>
            </span>
            <Button variant="ghost" size="sm" onClick={() => setPendingStandard(null)}>
              Write your own instead
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Adoption + signing officer configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Adoption details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium">How is this resolution adopted?</Label>
            <RadioGroup
              value={adoptionMode}
              onValueChange={(v) => setAdoptionMode(v as 'meeting' | 'written')}
              className="mt-2 space-y-2"
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem value="meeting" id="adopt-meeting" className="mt-1" />
                <Label htmlFor="adopt-meeting" className="font-normal">
                  At an Officer Meeting (record vote count)
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="written" id="adopt-written" className="mt-1" />
                <Label htmlFor="adopt-written" className="font-normal">
                  By written adoption of the undersigned
                </Label>
              </div>
            </RadioGroup>
          </div>

          {adoptionMode === 'meeting' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="votes-for" className="text-sm">Votes for</Label>
                <Input
                  id="votes-for"
                  type="number"
                  min={0}
                  value={votesFor}
                  onChange={(e) => setVotesFor(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="votes-against" className="text-sm">Votes against</Label>
                <Input
                  id="votes-against"
                  type="number"
                  min={0}
                  value={votesAgainst}
                  onChange={(e) => setVotesAgainst(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="adopt-meeting-date" className="text-sm">Meeting date</Label>
                <Input
                  id="adopt-meeting-date"
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="signing-officer-name" className="text-sm">Signing officer name</Label>
              <Input
                id="signing-officer-name"
                value={signingOfficerName}
                onChange={(e) => setSigningOfficerName(e.target.value)}
                placeholder={treasurySecretaryProfile?.full_name || 'Full name'}
              />
            </div>
            <div>
              <Label htmlFor="signing-officer-office" className="text-sm">Office</Label>
              <Input
                id="signing-officer-office"
                value={signingOfficerOffice}
                onChange={(e) => setSigningOfficerOffice(e.target.value)}
                placeholder={unaData?.primary_officer_role || 'Secretary Treasurer'}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Required Documents */}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Required Documents:</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <SealApplyCheckbox
              available={seal.sealAvailable}
              checked={seal.applySeal}
              onChange={seal.setApplySeal}
            />
            {requiredDocuments.map((docReq) => {
              const { status, doc } = getDocumentStatus(docReq.key);

              return (
                <div
                  key={docReq.key}
                  className="border rounded-lg p-4 flex items-start justify-between gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <h5 className="font-medium">{docReq.title}</h5>
                      {docReq.requiresSignature && doc?.status !== 'finalized' && (
                        <Badge variant="outline" className="text-xs">
                          Signature Required
                        </Badge>
                      )}
                    </div>
                    {doc && (
                      <div className="mt-2">
                        <Badge 
                          variant={doc.status === 'finalized' || status === 'complete' ? 'default' : 'secondary'}
                        >
                          {doc.status === 'finalized' ? 'Signed & Complete' :
                           status === 'complete' ? 'Generated' : 
                           status === 'needs_signature' ? 'Needs Signature' : 
                           'Generated'}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {!doc ? (
                      <Button
                        onClick={() => handleGenerateDocument(docReq)}
                        disabled={generating === docReq.key}
                      >
                        {generating === docReq.key ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          'Generate'
                        )}
                      </Button>
                    ) : (
                      <>
                        {doc.status === 'draft' && docReq.requiresSignature && (
                          <Button onClick={() => handleCollectSignature(doc)}>
                            Sign & Finalize
                          </Button>
                        )}
                        
                        {doc && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDocument(doc)}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Preview
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await downloadDocumentById(doc.id, { underscoreFilename: true });
                                  
                                  toast({
                                    title: 'Download Started',
                                    description: 'Document is downloading...',
                                  });
                                } catch (error) {
                                  console.error('Download error:', error);
                                  toast({
                                    title: 'Download Failed',
                                    description: 'Could not download document',
                                    variant: 'destructive',
                                  });
                                }
                              }}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                            
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedDocForEmail(doc.id);
                                setEmailWizardOpen(true);
                              }}
                              className="gap-1"
                            >
                              <Mail className="h-3 w-3" />
                              Email
                            </Button>
                            
                            {doc.merge_field_data?.requires_signature !== false && task.una_id && (
                          <div className="w-full">
                            <DocumentSigningControl
                              doc={doc}
                              unaId={task.una_id}
                              sealEnabled={seal.sealAvailable}
                              onChanged={loadDocuments}
                            />
                          </div>
                        )}
                            
                            <DocumentUploadButton
                              documentId={doc.id}
                              documentName={doc.document_name}
                              onUploadComplete={() => loadDocuments()}
                              fileUrl={doc.file_url}
                              hasUploadedFile={doc.file_path?.includes('-uploaded.pdf')}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Complete Phase Button - Always show, but disable when not ready */}
      <Alert className={documents.length < 1 ? 'border-muted' : 'border-green-200 bg-green-50'}>
        <AlertDescription>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {documents.length >= 1 ? 'Documents Ready' : 'Complete Phase 1'}
              </p>
              <p className="text-sm text-muted-foreground">
                {documents.length >= 1 
                  ? 'At least one document prepared. Ready to proceed.'
                  : 'Generate at least one document to continue.'}
              </p>
            </div>
            <Button 
              onClick={handleMarkPhaseComplete}
              disabled={documents.length < 1}
            >
              Complete Phase 1
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      {/* Modals */}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}

      {signatureWizardOpen && selectedDocForSignature && (
        <Dialog open={signatureWizardOpen} onOpenChange={(open) => {
          if (!open) {
            setSignatureWizardOpen(false);
            setSelectedDocForSignature(null);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <FinalizeWithSignatures
              documentTitle={selectedDocForSignature.document_name}
              treasurySecretaryName={unaData?.treasury_secretary_name || 'Secretary Treasurer'}
              primaryOfficerRoleLabel={unaData?.primary_officer_role || 'Secretary Treasurer'}
              requiredSigners={['treasury_secretary']}
              onFinalize={handleSignaturesComplete}
              onCancel={() => {
                setSignatureWizardOpen(false);
                setSelectedDocForSignature(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {selectedDocForEmail && (
        <DocumentEmailWizard
          open={emailWizardOpen}
          onOpenChange={(open) => {
            setEmailWizardOpen(open);
            if (!open) setSelectedDocForEmail(null);
          }}
          preSelectedDocumentId={selectedDocForEmail}
          taskId={task.id}
          unaId={task.una_id}
        />
      )}
    </div>
  );
}